import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deliveryMethodHelpers, useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/context/AppContext'
import services from '@/services/services'

import { getFieldErrors, getSuggestedAddress } from './helpers'
import { transformRequestValidateAddress, transformResponseAddressValidation } from './transformers'

const useAddressValidation = () => {
  const { context } = useContext(AppContext)
  const { t } = useTranslation()
  const { fetchData, loading } = useFetch(services.validateAddress, {
    doInitialRequest: false,
    transformRequest: transformRequestValidateAddress,
    transformResponse: transformResponseAddressValidation,
  })
  const [fieldErrors, setFieldErrors] = useState(null)
  const [suggestedAddress, setSuggestedAddress] = useState(null)
  const [showSuggestion, setShowSuggestion] = useState(false)

  const clearFieldErrors = () => setFieldErrors(null)

  const closeSuggestion = () => setShowSuggestion(false)

  const openSuggestion = suggestion => {
    setSuggestedAddress(
      getSuggestedAddress(context.orderInfo.shipping_address, suggestion, context?.allowed_region?.provinces)
    )
    setShowSuggestion(true)
  }

  const canContinueAfterValidation = validation => {
    if (validation.valid) return true

    if (validation.suggestion) {
      openSuggestion(validation.suggestion)

      return false
    }

    const errors = getFieldErrors(validation.error_codes, t)

    setFieldErrors(errors)

    return !errors
  }

  const validateAddress = async () => {
    const requiresAddress = context.orderInfo.deliveryMethod?.requires_address
    const address = deliveryMethodHelpers().getAddress(context.orderInfo)

    if (!requiresAddress || !address) return true

    clearFieldErrors()

    try {
      const validation = await fetchData({
        orderId: context.orderInfo.id,
        returnId: context.orderInfo.returnId,
        address,
        deliveryMethodId: context.orderInfo.deliveryMethod.id,
      })

      return canContinueAfterValidation(validation)
    } catch {
      return true
    }
  }

  return {
    validateAddress,
    validatingAddress: loading,
    fieldErrors,
    clearFieldErrors,
    suggestedAddress,
    showSuggestion,
    closeSuggestion,
  }
}

export default useAddressValidation
